// Enums matching backend Prisma schema
export enum ProjectStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  EXPIRED = 'EXPIRED',
}

export enum ProjectScope {
  GLOBAL = 'GLOBAL',
  LOCAL = 'LOCAL',
  GLOCAL = 'GLOCAL',
}

export enum ProjectType {
  REFORESTATION = 'REFORESTATION',
  RENEWABLE_ENERGY = 'RENEWABLE_ENERGY',
  ENERGY_EFFICIENCY = 'ENERGY_EFFICIENCY',
  BIOCHAR = 'BIOCHAR',
  DIRECT_AIR_CAPTURE = 'DIRECT_AIR_CAPTURE',
  CLEAN_COOKSTOVES = 'CLEAN_COOKSTOVES',
  WATER_PURIFICATION = 'WATER_PURIFICATION',
  WASTE_MANAGEMENT = 'WASTE_MANAGEMENT',
  SUSTAINABLE_AGRICULTURE = 'SUSTAINABLE_AGRICULTURE',
  BLUE_CARBON = 'BLUE_CARBON',
  OTHER = 'OTHER',
}

export enum Region {
  AFRICA = 'AFRICA',
  AMERICAS = 'AMERICAS',
  ASIA = 'ASIA',
  EUROPE = 'EUROPE',
  OCEANIA = 'OCEANIA',
  MULTI_REGIONAL = 'MULTI_REGIONAL',
}

export enum CertificationStandard {
  GOLD_STANDARD = 'GOLD_STANDARD',
  VERRA_VCS = 'VERRA_VCS',
  CERTIFIED_DAC = 'CERTIFIED_DAC',
  PLAN_VIVO = 'PLAN_VIVO',
  CLIMATE_ACTION_RESERVE = 'CLIMATE_ACTION_RESERVE',
  AMERICAN_CARBON_REGISTRY = 'AMERICAN_CARBON_REGISTRY',
  UNCERTIFIED = 'UNCERTIFIED',
  OTHER = 'OTHER',
}

export enum CoBenefit {
  BIODIVERSITY = 'BIODIVERSITY',
  CLEAN_WATER = 'CLEAN_WATER',
  LOCAL_COMMUNITIES = 'LOCAL_COMMUNITIES',
  EDUCATION = 'EDUCATION',
  WOMEN_EMPOWERMENT = 'WOMEN_EMPOWERMENT',
  HEALTH = 'HEALTH',
  FOREST_PROTECTION = 'FOREST_PROTECTION',
  LOCAL_JOBS = 'LOCAL_JOBS',
  RENEWABLE_ENERGY = 'RENEWABLE_ENERGY',
  POVERTY_REDUCTION = 'POVERTY_REDUCTION',
  FOOD_SECURITY = 'FOOD_SECURITY',
  CLIMATE_RESILIENCE = 'CLIMATE_RESILIENCE',
}

// Main Project interface
export interface Project {
  id: string;
  slug: string;

  // Multilingual content
  nameEN: string;
  nameFR: string;
  nameNL: string;
  shortDescriptionEN: string;
  shortDescriptionFR: string;
  shortDescriptionNL: string;
  longDescriptionEN: string;
  longDescriptionFR: string;
  longDescriptionNL: string;
  certificateTextEN?: string | null;
  certificateTextFR?: string | null;
  certificateTextNL?: string | null;
  socialTextEN?: string | null;
  socialTextFR?: string | null;
  socialTextNL?: string | null;

  // Pricing
  pricePerTonne: number;

  // Classification
  projectType: ProjectType;
  projectScope: ProjectScope;
  region: Region;
  location?: string | null;
  country?: string | null;

  // Certification
  certificationStandard: CertificationStandard;
  certificationYear?: number | null;
  isCertifiedOffset: boolean;
  certificationBadgeS3Key?: string | null;

  // Media (S3 Keys and URLs)
  mainImageS3Key?: string | null;
  mainImageUrl?: string | null;
  certificateImageS3Key?: string | null;
  certificateImageUrl?: string | null;
  socialShareImageS3Key?: string | null;
  socialShareImageUrl?: string | null;

  // Impact metrics (per tonne CO2)
  savedTreesPerTonne?: number | null;
  cleanWaterLitersPerTonne?: number | null;
  peopleCleanWaterPerTonne?: number | null;
  cookstovesPerTonne?: number | null;
  householdsSuppliedPerTonne?: number | null;
  protectedAresPerTonne?: number | null;
  annualCO2Reduction?: number | null;
  annualBeneficiaries?: number | null;

  // Co-benefits
  coBenefits: CoBenefit[];

  // Certificate configuration
  projectIcons?: string | null; // Comma-separated icon IDs (e.g., "01,03,05")
  certificateEquivalences?: string | null; // Comma-separated slugs (e.g., "streaming_harry_potter,ski_days")

  // Status & lifecycle
  status: ProjectStatus;
  isVisible: boolean;
  isDefault: boolean;
  isFeatured: boolean;
  startDate?: string | null;
  expirationDate?: string | null;

  // Odoo integration
  odooProductId?: number | null;

  // Audit
  createdAt: string;
  updatedAt: string;
}

// DTO for creating a new project
export interface CreateProjectDTO {
  slug: string;

  // Multilingual content (all required)
  nameEN: string;
  nameFR: string;
  nameNL: string;
  shortDescriptionEN: string;
  shortDescriptionFR: string;
  shortDescriptionNL: string;
  longDescriptionEN: string;
  longDescriptionFR: string;
  longDescriptionNL: string;
  certificateTextEN?: string;
  certificateTextFR?: string;
  certificateTextNL?: string;
  socialTextEN?: string;
  socialTextFR?: string;
  socialTextNL?: string;

  // Pricing
  pricePerTonne: number;

  // Classification
  projectType: ProjectType;
  projectScope: ProjectScope;
  region: Region;
  location?: string;
  country?: string;

  // Certification
  certificationStandard: CertificationStandard;
  certificationYear?: number;
  isCertifiedOffset: boolean;

  // Impact metrics (all optional)
  savedTreesPerTonne?: number;
  cleanWaterLitersPerTonne?: number;
  peopleCleanWaterPerTonne?: number;
  cookstovesPerTonne?: number;
  householdsSuppliedPerTonne?: number;
  protectedAresPerTonne?: number;
  annualCO2Reduction?: number;
  annualBeneficiaries?: number;

  // Co-benefits
  coBenefits: CoBenefit[];

  // Certificate configuration
  projectIcons?: string;
  certificateEquivalences?: string;

  // Status
  status?: ProjectStatus;
  isVisible?: boolean;
  isDefault?: boolean;
  isFeatured?: boolean;
  startDate?: string;
  expirationDate?: string;
}

// DTO for updating a project (all fields optional except id)
export interface UpdateProjectDTO {
  slug?: string;

  // Multilingual content
  nameEN?: string;
  nameFR?: string;
  nameNL?: string;
  shortDescriptionEN?: string;
  shortDescriptionFR?: string;
  shortDescriptionNL?: string;
  longDescriptionEN?: string;
  longDescriptionFR?: string;
  longDescriptionNL?: string;
  certificateTextEN?: string;
  certificateTextFR?: string;
  certificateTextNL?: string;
  socialTextEN?: string;
  socialTextFR?: string;
  socialTextNL?: string;

  // Pricing
  pricePerTonne?: number;

  // Classification
  projectType?: ProjectType;
  projectScope?: ProjectScope;
  region?: Region;
  location?: string;
  country?: string;

  // Certification
  certificationStandard?: CertificationStandard;
  certificationYear?: number;
  isCertifiedOffset?: boolean;

  // Impact metrics
  savedTreesPerTonne?: number;
  cleanWaterLitersPerTonne?: number;
  peopleCleanWaterPerTonne?: number;
  cookstovesPerTonne?: number;
  householdsSuppliedPerTonne?: number;
  protectedAresPerTonne?: number;
  annualCO2Reduction?: number;
  annualBeneficiaries?: number;

  // Co-benefits
  coBenefits?: CoBenefit[];

  // Certificate configuration
  projectIcons?: string;
  certificateEquivalences?: string;

  // Status
  status?: ProjectStatus;
  isVisible?: boolean;
  isDefault?: boolean;
  isFeatured?: boolean;
  startDate?: string;
  expirationDate?: string;

  // Odoo integration
  odooProductId?: number;
}

// Filters for project search
export interface ProjectFilters {
  q?: string; // Search query
  status?: ProjectStatus;
  projectType?: ProjectType;
  region?: Region;
  certificationStandard?: CertificationStandard;
  isCertifiedOffset?: boolean;
  page?: number;
  limit?: number;
}

// Image upload response
export interface ImageUploadResponse {
  s3Key: string;
  url: string;
  type: 'main' | 'certificate' | 'social';
}

// Helper type for dynamic impact metrics based on projectType
export const PROJECT_TYPE_IMPACT_METRICS: Record<ProjectType, string[]> = {
  [ProjectType.REFORESTATION]: ['savedTreesPerTonne', 'protectedAresPerTonne', 'annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.RENEWABLE_ENERGY]: ['householdsSuppliedPerTonne', 'annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.CLEAN_COOKSTOVES]: ['cookstovesPerTonne', 'cleanWaterLitersPerTonne', 'peopleCleanWaterPerTonne', 'annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.WATER_PURIFICATION]: ['cleanWaterLitersPerTonne', 'peopleCleanWaterPerTonne', 'annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.BLUE_CARBON]: ['protectedAresPerTonne', 'annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.BIOCHAR]: ['annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.DIRECT_AIR_CAPTURE]: ['annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.ENERGY_EFFICIENCY]: ['householdsSuppliedPerTonne', 'annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.WASTE_MANAGEMENT]: ['annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.SUSTAINABLE_AGRICULTURE]: ['annualCO2Reduction', 'annualBeneficiaries'],
  [ProjectType.OTHER]: ['annualCO2Reduction', 'annualBeneficiaries'],
};

// Certificate configuration options
export interface ProjectIconOption {
  id: string;
  labelEN: string;
  labelFR: string;
  labelNL: string;
}

export interface EquivalenceOption {
  slug: string;
  labelEN: string;
  labelFR: string;
  labelNL: string;
  icon: string;
}

// Partner company configuration for project restrictions
export interface PartnerCompanyConfig {
  id: string;
  companyId: string;
  projectId: string;
  pricePerTonne: number | null;
  isVisible: boolean;
  isDefault: boolean;
  company: {
    id: string;
    name: string;
    hasRestrictedProjects: boolean;
  };
}

export interface CreatePartnerCompanyDTO {
  companyId: string;
  pricePerTonne?: number;
  isVisible?: boolean;
  isDefault?: boolean;
}

export interface UpdatePartnerCompanyDTO {
  pricePerTonne?: number | null;
  isVisible?: boolean;
  isDefault?: boolean;
}

export interface CompanySearchResult {
  id: string;
  name: string;
  hasRestrictedProjects: boolean;
  _count: {
    partnerProjects: number;
  };
}

// Odoo product for project configuration
export interface OdooProduct {
  id: number;
  name: string;
  code: string | null;
  price: number;
}
